/**
 * Asset Paths
 *
 * Centralized paths for generated assets (tokens, conditions, etc.)
 * All paths are relative to /public/assets/
 */

import { trpc } from './trpc';
import { normalizeTokenImageKey, useTokenImageStore } from '../stores/tokenImageStore';
import { createFallbackTokenImage, isFallbackTokenImage, type TokenFallbackKind } from './tokenFallbacks';

const BASE = '/assets';

// Character token images by name (lowercase, no spaces)
export const CHARACTER_TOKENS: Record<string, string> = {
  skolnit: `${BASE}/characters/skolnit.png`,
  madjuju: `${BASE}/characters/madjuju.png`,
  'mad juju': `${BASE}/characters/madjuju.png`,
  bobby: `${BASE}/characters/bobby.png`,
  bobbyecho: `${BASE}/characters/bobbyecho.png`,
  dax: `${BASE}/characters/dax.png`,
  ashala: `${BASE}/characters/ashala.png`,
};

// Mob token images by creature type (lowercase)
export const MOB_TOKENS: Record<string, string> = {
  goblin: `${BASE}/mobs/goblin.png`,
  'goblin boss': `${BASE}/mobs/goblin.png`,
  skeleton: `${BASE}/mobs/skeleton.png`,
  orc: `${BASE}/mobs/orc.png`,
  kobold: `${BASE}/mobs/kobold.png`,
  wolf: `${BASE}/mobs/wolf.png`,
  'dire wolf': `${BASE}/mobs/wolf.png`,
  zombie: `${BASE}/mobs/zombie.png`,
  bandit: `${BASE}/mobs/bandit.png`,
  cultist: `${BASE}/mobs/cultist.png`,
  spider: `${BASE}/mobs/spider.png`,
  'giant spider': `${BASE}/mobs/spider.png`,
  rat: `${BASE}/mobs/rat.png`,
  'giant rat': `${BASE}/mobs/rat.png`,
};

// Condition icons by condition type
export const CONDITION_ICONS: Record<string, string> = {
  blinded: `${BASE}/conditions/blinded.png`,
  charmed: `${BASE}/conditions/charmed.png`,
  deafened: `${BASE}/conditions/deafened.png`,
  frightened: `${BASE}/conditions/frightened.png`,
  grappled: `${BASE}/conditions/grappled.png`,
  incapacitated: `${BASE}/conditions/incapacitated.png`,
  invisible: `${BASE}/conditions/invisible.png`,
  paralyzed: `${BASE}/conditions/paralyzed.png`,
  petrified: `${BASE}/conditions/petrified.png`,
  poisoned: `${BASE}/conditions/poisoned.png`,
  prone: `${BASE}/conditions/prone.png`,
  restrained: `${BASE}/conditions/restrained.png`,
  stunned: `${BASE}/conditions/stunned.png`,
  unconscious: `${BASE}/conditions/unconscious.png`,
  exhaustion: `${BASE}/conditions/exhaustion.png`,
  bloodied: `${BASE}/conditions/bloodied.png`,
};

/**
 * Strip trailing instance number from mob display names.
 * "Ghoul 1" → "ghoul", "Orc War Chief" → "orc war chief"
 */
function baseName(name: string | undefined | null): string {
  return normalizeTokenImageKey(name);
}

/**
 * Get character token URL by name
 */
export function getCharacterToken(name: string): string | undefined {
  return CHARACTER_TOKENS[name.toLowerCase()];
}

/**
 * Get mob token URL by creature name/type
 */
export function getMobToken(name: string): string | undefined {
  const key = name.toLowerCase();
  // Direct match
  if (MOB_TOKENS[key]) return MOB_TOKENS[key];
  // Partial match (e.g., "Goblin 1" matches "goblin")
  for (const [mobKey, url] of Object.entries(MOB_TOKENS)) {
    if (key.includes(mobKey) || mobKey.includes(key)) {
      return url;
    }
  }
  return undefined;
}

/**
 * Resolve mob image URL using fallback chain:
 * 1. tokenImageStore cache (localStorage, from previous sessions or AI generation)
 * 2. Local PNG assets (MOB_TOKENS lookup)
 * 3. Open5e img_main URL (if provided)
 * 4. undefined (falls back to colored shape in TokenRenderer)
 */
export function resolveMobImage(name: string, imgMain?: string | null): string | undefined {
  const key = baseName(name);

  // 1. Check localStorage cache (includes AI-generated tokens)
  try {
    const cached = localStorage.getItem('machina:mob-token-images');
    if (cached) {
      const images = JSON.parse(cached) as Record<string, string>;
      if (images[key]) return images[key];
    }
  } catch {
    // ignore parse errors
  }

  // 2. Check local PNG assets
  const localToken = getMobToken(name);
  if (localToken) return localToken;

  // 3. Open5e img_main
  if (imgMain) return imgMain;

  return undefined;
}

// Track in-flight generations to avoid duplicates
const pendingGenerations = new Set<string>();

/**
 * Generate token images for creatures that have no local or cached image.
 * Calls FLUX via the API, caches results in localStorage, and invokes
 * the callback as each image arrives so tokens can update in-place.
 */
export async function generateMissingTokens(
  creatureNames: string[],
  onGenerated?: (name: string, imageUrl: string) => void,
  options?: { kind?: TokenFallbackKind },
): Promise<void> {
  // Deduplicate by base name, filter out already-resolved ones
  const seen = new Set<string>();
  const missing: string[] = [];
  for (const raw of creatureNames) {
    const key = baseName(raw);
    // A cached PLACEHOLDER doesn't count as resolved — retry generation for it,
    // otherwise a single transient failure pins initials art permanently.
    const existing = resolveMobImage(raw);
    const resolved = existing && !isFallbackTokenImage(existing);
    if (seen.has(key) || resolved || pendingGenerations.has(key)) continue;
    seen.add(key);
    missing.push(key);
  }

  if (missing.length === 0) return;

  // Mark as pending
  missing.forEach(n => pendingGenerations.add(n));

  // Fire all in parallel — each resolves independently
  await Promise.allSettled(
    missing.map(async (name) => {
      try {
        const result = await trpc.maps.generateToken.mutate({ creatureName: name });
        useTokenImageStore.getState().setTokenImage(name, result.imageUrl);
        onGenerated?.(name, result.imageUrl);
        console.log(`[TokenGen] Generated token for: ${name}`);
      } catch (err) {
        console.warn(`[TokenGen] Failed to generate token for: ${name}`, err);
        const fallbackUrl = createFallbackTokenImage(name, options?.kind ?? 'monster');
        useTokenImageStore.getState().setTokenImage(name, fallbackUrl);
        onGenerated?.(name, fallbackUrl);
        console.log(`[TokenGen] Using fallback token for: ${name}`);
      } finally {
        pendingGenerations.delete(name);
      }
    }),
  );
}

/**
 * Get condition icon URL
 */
export function getConditionIcon(type: string): string | undefined {
  return CONDITION_ICONS[type.toLowerCase()];
}
